import { makeAutoObservable, runInAction } from 'mobx';
import type { Product } from '@/core/domain/entities/Product';
import type { GetBrandProductsUseCase } from '@/core/application/use-cases/product/GetBrandProductsUseCase';

export class BrandProductsStore {
  slug = '';
  contextKey = '';
  products: Product[] = [];
  count = 0;
  page = 0;
  hasMore = true;
  isLoading = false;
  error: string | null = null;
  private generation = 0;

  constructor(private readonly getProducts: GetBrandProductsUseCase) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  reset() {
    this.generation += 1;
    this.slug = '';
    this.contextKey = '';
    this.products = [];
    this.count = 0;
    this.page = 0;
    this.hasMore = true;
    this.isLoading = false;
    this.error = null;
  }

  async open(slug: string, contextKey: string) {
    this.reset();
    this.slug = slug;
    this.contextKey = contextKey;
    await this.loadNext();
  }

  async loadNext() {
    if (!this.slug || this.isLoading || !this.hasMore) return;
    const generation = this.generation;
    const page = this.page + 1;
    this.isLoading = true;
    this.error = null;
    try {
      const result = await this.getProducts.execute(this.slug, page);
      runInAction(() => {
        if (generation !== this.generation) return;
        const seen = new Set(this.products.map(product => product.id));
        this.products = [...this.products, ...result.results.filter(product => {
          if (seen.has(product.id)) return false;
          seen.add(product.id);
          return true;
        })];
        this.count = result.count;
        this.hasMore = Boolean(result.next);
        this.page = page;
      });
    } catch {
      runInAction(() => {
        if (generation === this.generation) this.error = "We couldn't load these products. Please try again.";
      });
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.isLoading = false;
      });
    }
  }
}
